import WorldMap from './worldmap';

// Define interfaces for tunnel algorithm
interface Node {
    x: number;
    z: number;
    direction: Direction;    // Direction object, not a string
    type: string;
    parent: Node | null;
    depth: number;
    isRoom: boolean;
    roomWidth?: number;
    roomHeight?: number;
    isExplored?: boolean;
    isConnector?: boolean;
}

interface Direction {
    x: number;
    z: number;
}

/**
 * Tunnel Algorithm for dungeon generation
 * This is a module that generates a procedural dungeon using a tunnel-based approach
 */
class TunnelAlgorithm {
    // Constants for dungeon generation
    private TUNNEL_WIDTH: number = 3;
    private MAX_TUNNEL_LENGTH: number = 30;
    private MIN_ROOM_SIZE = { width: 5, height: 4 };
    private MAX_ROOM_SIZE = { width: 9, height: 12 };
    private ROOM_PROBABILITY: number = 0.9;
    private TURN_CHANCE: number = 1.0; // 100% chance to turn at tunnel ends
    
    // Node types (matching JS implementation)
    private NODE_TYPES = {
        ROOM_END: 'room_end',
        TUNNEL_END: 'tunnel_end',
        TUNNEL_BRANCH: 'tunnel_branch',
        TUNNEL_CONNECTING: 'tunnel_connecting'
    };
    
    // Dungeon state
    private tiles: Map<string, number> = new Map(); // Map to store tile values by position string "x,z"
    private nodes: Node[] = []; // List of all nodes in the dungeon
    private positionStrings: Map<string, boolean> = new Map(); // Quick lookup for positions
    private nodesByPosition: Map<string, Node> = new Map(); // Map nodes by their position string
    private exploredNodes: Set<string> = new Set(); // Track which nodes have been explored
    private dungeonSeed: number = Math.random() * 1000000; // Seed for random generation
    private nodeConnections: { from: { x: number, z: number }, to: { x: number, z: number } }[] = []; // Track connections
    private rooms: { x: number, z: number, width: number, height: number }[] = [];

    // Directions (NESW)
    private DIRECTIONS: Record<string, Direction> = {
        NORTH: { x: 0, z: -1 },
        EAST: { x: 1, z: 0 },
        SOUTH: { x: 0, z: 1 },
        WEST: { x: -1, z: 0 }
    };

    // Map
    private worldMap: WorldMap;

    constructor() {
        this.initialize();
    }

    /**
     * Initialize the algorithm
     * @returns {{ worldMap: WorldMap, nodes: Node[] }} - Initialized state
     */
    public initialize(): { worldMap: WorldMap, nodes: Node[] } {
        // Reset the map and nodes
        this.worldMap = new WorldMap(16); // CHUNK_SIZE = 16
        this.tiles.clear();
        this.nodes = [];
        this.rooms = [];
        this.nodeConnections = []; // Reset node connections
        this.exploredNodes = new Set(); // Reset explored nodes
        this.positionStrings.clear();
        this.nodesByPosition.clear();

        // At least 2 paths, but no more than 4
        const initialPaths = Math.max(Math.floor(Math.random() * 4), 2);
        const initialDirections: Direction[] = [];

        // Get random but different directions for the first tunnels
        while (initialDirections.length < initialPaths) {
            // Get a random direction object
            const initialDirection = this.getRandomDirection();
            
            // Check if this direction is already in the array
            if (!initialDirections.some(d => d.x === initialDirection.x && d.z === initialDirection.z)) {
                initialDirections.push(initialDirection);
            }
        }

        // Start with a node at the center and dig tunnels in random directions
        for (const direction of initialDirections) {
            this.nodes.push({
                x: 0,
                z: 0,
                direction: direction,
                type: this.NODE_TYPES.TUNNEL_END,
                parent: null,
                depth: 0,
                isRoom: false
            });
        }

        return { worldMap: this.worldMap, nodes: this.nodes };
    }

    /**
     * Get a random direction (NORTH, EAST, SOUTH, WEST)
     * @returns {Direction} - Random Direction object (not a string)
     */
    private getRandomDirection(): Direction {
        const directions = Object.values(this.DIRECTIONS);
        return directions[Math.floor(Math.random() * directions.length)];
    }

    /**
     * Get perpendicular directions to the given direction
     * @param {Direction} direction - Direction to get perpendicular directions for
     * @returns {Direction[]} - Array of perpendicular Direction objects
     */
    private getPerpendicularDirections(direction: Direction): Direction[] {
        if (direction.x === 0) {
            // Direction is north or south, so return east and west
            return [this.DIRECTIONS.EAST, this.DIRECTIONS.WEST];
        } else {
            // Direction is east or west, so return north and south
            return [this.DIRECTIONS.NORTH, this.DIRECTIONS.SOUTH];
        }
    }

    /**
     * Get random room dimensions
     * @returns {{ width: number, height: number }} - Random room dimensions
     */
    private getRandomRoomSize(): { width: number, height: number } {
        return {
            width: this.MIN_ROOM_SIZE.width + Math.floor(Math.random() * (this.MAX_ROOM_SIZE.width - this.MIN_ROOM_SIZE.width + 1)),
            height: this.MIN_ROOM_SIZE.height + Math.floor(Math.random() * (this.MAX_ROOM_SIZE.height - this.MIN_ROOM_SIZE.height + 1))
        };
    }

    /**
     * Get a tile value directly (O(1) lookup)
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @returns {number | undefined} - Tile value (0=floor, 1=wall) or undefined
     */
    public getTileValue(x: number, z: number): number | undefined {
        return this.tiles.get(`${x},${z}`);
    }

    /**
     * Check if a tile is empty (not set)
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @returns {boolean} - True if the tile is empty
     */
    private isTileEmpty(x: number, z: number): boolean {
        return !this.tiles.has(`${x},${z}`);
    }

    /**
     * Check if a tile is a wall
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @returns {boolean} - True if the tile is a wall
     */
    private isTileWall(x: number, z: number): boolean {
        return this.tiles.get(`${x},${z}`) === 1;
    }

    /**
     * Check if a tile is a floor
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @returns {boolean} - True if the tile is a floor
     */
    private isTileFloor(x: number, z: number): boolean {
        return this.tiles.get(`${x},${z}`) === 0;
    }

    /**
     * Check if a tile is a wall or undefined (for area checking)
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @returns {boolean} - True if the tile is a wall or undefined
     */
    private isTileWallOrUndefined(x: number, z: number): boolean {
        const tile = this.tiles.get(`${x},${z}`);
        return tile === undefined || tile === 1;
    }

    /**
     * Set a tile value
     * @param {number} x - X coordinate
     * @param {number} z - Z coordinate
     * @param {number} value - Tile value (0=floor, 1=wall)
     */
    private setTile(x: number, z: number, value: number): void {
        this.tiles.set(`${x},${z}`, value);
        this.positionStrings.set(`${x},${z}`, true);
    }

    /**
     * Check if a rectangular area is available (all tiles are wall or undefined)
     * @param {number} startX - Start X coordinate
     * @param {number} startZ - Start Z coordinate
     * @param {number} width - Area width
     * @param {number} height - Area height
     * @param {Direction} direction - Direction of area
     * @returns {boolean} - True if the area is available
     */
    private isAreaAvailable(startX: number, startZ: number, width: number, height: number, direction: Direction): boolean {
        // Handle all directions
        // North/South direction (negative z or x)
        if (direction.x === 0 && direction.z === -1) { // NORTH
            for (let z = startZ; z >= startZ - height; z--) {
                for (let x = startX; x <= startX + width; x++) {
                    if (this.isTileFloor(x, z)) {
                        return false;
                    } // 0 = floor
                }
            }
        } else if (direction.x === -1 && direction.z === 0) { // WEST
            for (let z = startZ; z <= startZ + height; z++) {
                for (let x = startX; x >= startX - width; x--) {
                    if (this.isTileFloor(x, z)) {
                        return false;
                    }
                }
            }
        } else { // Positive z or x
            for (let z = startZ; z <= startZ + height; z++) {
                for (let x = startX; x <= startX + width; x++) {
                    if (this.isTileFloor(x, z)) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    /**
     * Dig a tunnel from a starting position in a direction
     * @param {number} startX - Start X coordinate
     * @param {number} startZ - Start Z coordinate
     * @param {Direction} direction - Direction to dig
     * @param {number} length - Maximum tunnel length
     * @returns {{ endX: number, endZ: number, length: number, isConnectingTunnel: boolean }} - Tunnel data
     */
    public digTunnel(startX: number, startZ: number, direction: Direction, length: number): { endX: number, endZ: number, length: number, isConnectingTunnel: boolean } | null {
        const tunnelHalfWidth = Math.floor(this.TUNNEL_WIDTH / 2);
        
        let isConnectingTunnel = false;
        
        // Check if tunnel path is free - skip checking the starting position itself
        // since it might be a floor tile (from a previous tunnel end)
        let tunnelMaxLength = 0;
        for (let i = 2; i <= length; i++) { // Start from 2 to skip the start position and the outer tile 
            const checkX = startX + direction.x * i;
            const checkZ = startZ + direction.z * i;

            let tunnelAreaFree = true;
            // Check the width of the tunnel at this point
            for (let w = -tunnelHalfWidth; w <= tunnelHalfWidth; w++) {
                let wx: number, wz: number;
                if (direction.x === 0) {
                    // North/South direction
                    wx = checkX + w;
                    wz = checkZ;
                } else {
                    // East/West direction
                    wx = checkX;
                    wz = checkZ + w;
                }

                if (this.isTileFloor(wx, wz)) {
                    tunnelAreaFree = false;
                    break;
                }
            }

            if (!tunnelAreaFree) {
                tunnelMaxLength = i + 1;
                isConnectingTunnel = true;
                break;
            }

            tunnelMaxLength = i;
        }

        // If we can't dig at least 3 steps, don't bother
        if (tunnelMaxLength < 3) {
            return null;
        }

        // Dig the tunnel up to the maximum available length
        const actualEndX = startX + direction.x * tunnelMaxLength;
        const actualEndZ = startZ + direction.z * tunnelMaxLength;

        // Create the tunnel floor tiles
        for (let i = -1; i <= tunnelMaxLength; i++) {
            const tunnelX = startX + direction.x * i;
            const tunnelZ = startZ + direction.z * i;

            // Dig a 3x3 tunnel (or whatever TUNNEL_WIDTH is)
            for (let w = -tunnelHalfWidth; w <= tunnelHalfWidth; w++) {
                if (direction.x === 0) {
                    // North/South direction
                    this.setTile(tunnelX + w, tunnelZ, 0); // 0 = floor
                } else {
                    // East/West direction
                    this.setTile(tunnelX, tunnelZ + w, 0); // 0 = floor
                }
            }
        }

        // Return the end coordinates and actual length
        return {
            endX: actualEndX,
            endZ: actualEndZ,
            length: tunnelMaxLength,
            isConnectingTunnel: isConnectingTunnel
        };
    }

    /**
     * Get the opposite direction
     * @param {string} direction - Direction to get the opposite of
     * @returns {string} - Opposite direction
     */
    private getOppositeDirection(direction: string): string {
        if (direction === 'NORTH') return 'SOUTH';
        if (direction === 'SOUTH') return 'NORTH';
        if (direction === 'EAST') return 'WEST';
        if (direction === 'WEST') return 'EAST';
        return 'none';
    }

    /**
     * Create a room at specified position with given dimensions
     * @param {number} startX - Start X coordinate
     * @param {number} startZ - Start Z coordinate
     * @param {number} width - Room width
     * @param {number} height - Room height
     * @param {Direction} direction - Direction of room creation
     * @returns {{ x: number, z: number, width: number, height: number }} - Room details
     */
    private createRoom(startX: number, startZ: number, width: number, height: number, direction: Direction): { x: number, z: number, width: number, height: number } {
        // Create floor tiles for the room
        if (direction === this.DIRECTIONS.NORTH) {
            for (let z = startZ; z > startZ - height; z--) {
                for (let x = startX; x < startX + width; x++) {
                    this.setTile(x, z, 0); // 0 = floor
                }
            }
        } else if (direction === this.DIRECTIONS.WEST) {
            for (let z = startZ; z < startZ + height; z++) {
                for (let x = startX; x > startX - width; x--) {
                    this.setTile(x, z, 0); // 0 = floor
                }
            }
        } else { // Positive z or x
            for (let z = startZ; z < startZ + height; z++) {
                for (let x = startX; x < startX + width; x++) {
                    this.setTile(x, z, 0); // 0 = floor
                }
            }
        }

        // Add the room to our rooms list
        const room = {
            x: startX,
            z: startZ,
            width: width,
            height: height
        };
        
        this.rooms.push(room);
        
        return room;
    }

    /**
     * Connect a room to a tunnel
     * @param {number} roomX - Room center X
     * @param {number} roomZ - Room center Z
     * @param {number} roomWidth - Room width
     * @param {number} roomHeight - Room height
     * @param {number} tunnelX - Tunnel end X
     * @param {number} tunnelZ - Tunnel end Z
     * @param {Direction} direction - Direction from tunnel to room
     */
    private connectRoomToTunnel(
        roomX: number, 
        roomZ: number, 
        roomWidth: number, 
        roomHeight: number, 
        tunnelX: number, 
        tunnelZ: number, 
        direction: Direction
    ): void {
        // Calculate connection point on the room
        let connectionX: number, connectionZ: number;

        if (direction.x === 0 && direction.z === -1) { // NORTH
            connectionX = roomX + Math.floor(roomWidth / 2);
            connectionZ = roomZ;
        } else if (direction.x === -1 && direction.z === 0) { // WEST
            connectionX = roomX;
            connectionZ = roomZ + Math.floor(roomHeight / 2);
        } else { // Positive z or x (SOUTH or EAST)
            connectionX = roomX + Math.floor(roomWidth / 2);
            connectionZ = roomZ + Math.floor(roomHeight / 2);
        }

        // Dig a connecting passage from the tunnel to the room
        const tunnelHalfWidth = 1; // Math.floor(TUNNEL_WIDTH / 2) where TUNNEL_WIDTH = 3

        if (direction.x === 0) {
            // North/South connection
            for (let x = Math.min(tunnelX, connectionX); x <= Math.max(tunnelX, connectionX); x++) {
                for (let w = -tunnelHalfWidth; w <= tunnelHalfWidth; w++) {
                    this.setTile(x, tunnelZ + w, 0); // 0 = floor
                }
            }

            for (let z = Math.min(tunnelZ, connectionZ); z <= Math.max(tunnelZ, connectionZ); z++) {
                for (let w = -tunnelHalfWidth; w <= tunnelHalfWidth; w++) {
                    this.setTile(connectionX + w, z, 0); // 0 = floor
                }
            }
        } else {
            // East/West connection
            for (let z = Math.min(tunnelZ, connectionZ); z <= Math.max(tunnelZ, connectionZ); z++) {
                for (let w = -tunnelHalfWidth; w <= tunnelHalfWidth; w++) {
                    this.setTile(tunnelX + w, z, 0); // 0 = floor
                }
            }

            for (let x = Math.min(tunnelX, connectionX); x <= Math.max(tunnelX, connectionX); x++) {
                for (let w = -tunnelHalfWidth; w <= tunnelHalfWidth; w++) {
                    this.setTile(x, connectionZ + w, 0); // 0 = floor
                }
            }
        }
    }

    /**
     * Process a node in the dungeon generation
     * @param {Node} node - The node to process
     * @returns {Node[]} - Array of new nodes created
     */
    public processNode(node: Node): Node[] {
        // Mark this node as explored so we don't process it again
        this.markNodeAsExplored(node);
        
        // New nodes that we'll create during processing
        const newNodes: Node[] = [];
        
        // Get the direction vector from the node's direction
        const directionVector = node.direction;
        if (!directionVector) {
            console.error('Invalid direction:', node.direction);
            return newNodes;
        }
        
        // Try to dig a tunnel in the direction of this node
        const tunnel = this.digTunnel(node.x, node.z, directionVector, this.MAX_TUNNEL_LENGTH);
        if (!tunnel) {
            return newNodes;
        }
        
        const { endX, endZ, length, isConnectingTunnel } = tunnel;
        
        // Attempt to place multiple rooms along the tunnel
        this.attemptMultipleRoomsAlongTunnel(node, { 
            x: endX, 
            z: endZ, 
            direction: node.direction, 
            type: this.NODE_TYPES.TUNNEL_END, 
            parent: node, 
            depth: node.depth + 1, 
            isRoom: false 
        }, directionVector, newNodes);
        
        // If the tunnel is connecting to another tunnel, create a new node at the end of the tunnel
        // and mark it as explored. This is to prevent the algorithm from creating a new node at the end of the tunnel
        if (isConnectingTunnel) {
            const collidingNode: Node = {
                x: endX,
                z: endZ,
                direction: node.direction,
                type: this.NODE_TYPES.TUNNEL_CONNECTING,
                parent: node,
                depth: node.depth + 1,
                isRoom: false
            };
            newNodes.push(collidingNode);
            this.nodeConnections.push({
                from: { x: node.x, z: node.z },
                to: { x: endX, z: endZ }
            });
            this.markNodeAsExplored(collidingNode);
            return newNodes;
        }
        
        // Decide whether to branch or continue at the end of the tunnel
        const turnChance = Math.random() < this.TURN_CHANCE;
        if (turnChance) {
            // Get multiple random directions for branching
            const possibleDirections: Direction[] = [];
            
            // Try to get 2 distinct directions that aren't opposite to our incoming direction
            for (let i = 0; i < 4; i++) { // Try up to 4 times to get 2 good directions
                if (possibleDirections.length >= 2) break;
                
                const randDir = this.getRandomDirection();
                
                // Skip directions that lead back where we came from
                if (randDir.x === -directionVector.x && randDir.z === -directionVector.z) {
                    continue;
                }
                
                // Avoid duplicate directions
                if (!possibleDirections.some(d => d.x === randDir.x && d.z === randDir.z)) {
                    possibleDirections.push(randDir);
                }
            }
            
            // Create new nodes for each direction
            possibleDirections.forEach(newDirection => {
                const newNode: Node = {
                    x: endX,
                    z: endZ,
                    direction: newDirection,
                    type: this.NODE_TYPES.TUNNEL_BRANCH,
                    parent: node,
                    depth: node.depth + 1,
                    isRoom: false
                };
                
                newNodes.push(newNode);
                
                // Track this connection
                this.nodeConnections.push({
                    from: { x: node.x, z: node.z },
                    to: { x: endX, z: endZ }
                });
            });
        } else {
            // Continue in the same direction
            const newNode: Node = {
                x: endX,
                z: endZ,
                direction: node.direction,
                type: this.NODE_TYPES.TUNNEL_END,
                parent: node,
                depth: node.depth + 1,
                isRoom: false
            };
            
            newNodes.push(newNode);
            
            // Track this connection
            this.nodeConnections.push({
                from: { x: node.x, z: node.z },
                to: { x: endX, z: endZ }
            });
        }
        
        return newNodes;
    }

    /**
     * Get the direction of the next node in a path
     * @param {Node} current - Current node
     * @param {Node} target - Target node
     * @returns {string} - Direction to move ('north', 'east', 'south', 'west')
     */
    public getNextNodeDirection(current: Node, target: Node): string {
        // Simple version - just determine direction based on position difference
        const dx = target.x - current.x;
        const dz = target.z - current.z;
        
        // Get the larger axis
        if (Math.abs(dx) > Math.abs(dz)) {
            return dx > 0 ? 'east' : 'west';
        } else {
            return dz > 0 ? 'south' : 'north';
        }
    }

    /**
     * Generate dungeon within a specified distance from a point
     * @param {number} centerX - Center X coordinate
     * @param {number} centerZ - Center Z coordinate
     * @param {number} distance - Distance from center to generate
     * @returns {{ worldMap: WorldMap, processedNodes: Node[], pendingNodes: Node[] }} - Generated dungeon data
     */
    public generateDungeon(centerX: number = 0, centerZ: number = 0, distance: number = 20): { worldMap: WorldMap, processedNodes: Node[], pendingNodes: Node[] } {
        // Queue of nodes to process
        let nodeQueue = [...this.nodes];
        
        // Process nodes until there are no more nodes within the distance
        let processedCount = 0;
        let nodesProcessedThisRun: Node[] = [];
        
        // Create a new filtered nodes array to avoid duplicate processing
        let filteredNodes: Node[] = [];
        
        while (nodeQueue.length > 0) {
            const node = nodeQueue.shift()!;
            
            // Create a unique key for this node to check if it's already processed
            const dirObj = node.direction;
            const nodeKey = `${node.x},${node.z},${dirObj.x},${dirObj.z}`;
            
            // Skip if this node has already been processed
            if (this.exploredNodes.has(nodeKey)) {
                continue;
            }
            
            // Check if this node is within the specified distance
            const nodeDistance = Math.sqrt(Math.pow(node.x - centerX, 2) + Math.pow(node.z - centerZ, 2));
            if (nodeDistance > distance) {
                // Keep the node for future processing, but don't process it now
                filteredNodes.push(node);
                continue;
            }
            
            // Process the node
            const newNodes = this.processNode(node);
            processedCount++;
            nodesProcessedThisRun.push(node);
            
            // Add new nodes to the queue for processing
            nodeQueue.push(...newNodes);
            
            // Also add new nodes to the filtered nodes list (these will replace the global nodes array)
            newNodes.forEach(newNode => {
                // Check if this node would be a duplicate
                const newNodeDirObj = newNode.direction;
                const newNodeKey = `${newNode.x},${newNode.z},${newNodeDirObj.x},${newNodeDirObj.z}`;
                if (!this.exploredNodes.has(newNodeKey)) {
                    filteredNodes.push(newNode);
                }
                
                // If any of the new nodes are very close to another node, mark as explored
                const closeNodes = this.nodes.filter(node => {
                    const distance = Math.sqrt(Math.pow(node.x - newNode.x, 2) + Math.pow(node.z - newNode.z, 2));
                    return distance < 8;
                });
                
                // Explore the close nodes
                closeNodes.forEach(closeNode => {
                    this.markNodeAsExplored(closeNode);
                });
                
                if (closeNodes.length > 0) {
                    this.markNodeAsExplored(newNode);
                }
            });
        }
        
        // Replace the global nodes array with our filtered version
        this.nodes = filteredNodes;
        
        return {
            worldMap: this.worldMap,
            processedNodes: nodesProcessedThisRun,
            pendingNodes: this.nodes
        };
    }

    /**
     * Get all tiles within a certain distance of a center point
     * @param {number} centerX - Center X coordinate
     * @param {number} centerZ - Center Z coordinate
     * @param {number} distanceX - X distance to include
     * @param {number} distanceZ - Z distance to include
     * @returns {Map<string, number>} - Map of position strings to tile values
     */
    public getTilesToRender(centerX: number, centerZ: number, distanceX: number, distanceZ: number): Map<string, number> {
        // Create a map to store the tiles
        const localMap = new Map<string, number>();
        
        // Calculate bounds
        const minX = Math.floor(centerX - distanceX);
        const maxX = Math.ceil(centerX + distanceX);
        const minZ = Math.floor(centerZ - distanceZ);
        const maxZ = Math.ceil(centerZ + distanceZ);
        const filteredWorldMap = this.worldMap.getTilesInArea(minX, maxX, minZ, maxZ);
        
        for (const [_, tile] of filteredWorldMap.entries()) {
            //const [x, z] = [key.x, key.z];
            const key = `${tile.x},${tile.z}`;
            //console.log("Key: ", key);
            // Calculate distance from center
            const dx = tile.x - centerX;
            const dz = tile.z - centerZ;
            const tileDistanceX = Math.abs(dx);
            const tileDistanceZ = Math.abs(dz);

            // If within the specified distance, add to result
            if (tileDistanceX <= distanceX && tileDistanceZ <= distanceZ) {
                //console.log("Added tile at ", tile.x, tile.z, " to local map");
                localMap.set(key, tile.value);
            }
        }
        
        // Add all tiles within the bounds
        for (let x = minX; x <= maxX; x++) {
            for (let z = minZ; z <= maxZ; z++) {
                const posKey = `${x},${z}`;
                const tileValue = this.tiles.get(posKey);
                
                // If the tile exists, add it to the render map
                if (tileValue !== undefined) {
                    localMap.set(posKey, tileValue);
                } else {
                    // For tiles that don't exist, default to wall
                    localMap.set(posKey, 1);
                }
            }
        }
        
        return localMap;
    }

    /**
     * Get all nodes (both pending and processed)
     * @returns {Object} - Object containing pendingNodes, processedNodes, and nodeConnections
     */
    public getAllNodes(): { pendingNodes: Node[], processedNodes: string[], nodeConnections: { from: { x: number, z: number }, to: { x: number, z: number } }[] } {
        // Create a deep copy of the nodes array to avoid external modification
        return {
            pendingNodes: this.nodes.map(node => ({ ...node })),
            processedNodes: Array.from(this.exploredNodes), // Returns array of string keys
            nodeConnections: [...this.nodeConnections]  // Return tracked connections
        };
    }

    /**
     * Mark a node as explored
     * @param {Node} node - The node to mark as explored
     * @returns {boolean} - True if the node was newly marked as explored
     */
    public markNodeAsExplored(node: Node): boolean {
        // Create a unique key for this node
        const dirObj = node.direction;
        const nodeKey = `${node.x},${node.z},${dirObj.x},${dirObj.z}`;
        
        // Check if already explored
        if (this.exploredNodes.has(nodeKey)) {
            return false;
        }
        
        // Add to explored nodes
        this.exploredNodes.add(nodeKey);
        node.isExplored = true;
        
        return true;
    }

    /**
     * Check if a node is explored
     * @param {Node} node - The node to check
     * @returns {boolean} - True if the node is explored
     */
    public isNodeExplored(node: Node): boolean {
        const posKey = `${node.x},${node.z}`;
        return this.exploredNodes.has(posKey);
    }

    /**
     * Function to attempt placing multiple rooms along a tunnel
     * @param {Node} startNode - Start node
     * @param {Node} endNode - End node
     * @param {Direction} direction - Tunnel direction
     * @param {Node[]} newNodes - Array of new nodes created
     */
    private attemptMultipleRoomsAlongTunnel(startNode: Node, endNode: Node, direction: Direction, newNodes: Node[]): void {
        const startX = startNode.x;
        const startZ = startNode.z;
        const endX = endNode.x;
        const endZ = endNode.z;
        
        // Calculate tunnel length
        const dx = endX - startX;
        const dz = endZ - startZ;
        const length = Math.sqrt(dx * dx + dz * dz);
        
        // Get perpendicular directions for room placement
        const perpDirections = this.getPerpendicularDirections(direction);
        
        // We will try placing rooms on both sides of the tunnel
        for (const perpDirection of perpDirections) {
            // Start from position 5 and go until (length - 5)
            const ROOM_STARTING_POSITION = 5;
            let currentPosition = ROOM_STARTING_POSITION;
            
            const ROOM_CHANCE = 0.8; // 80% chance to create a room
            const TUNNEL_WIDTH = 3;
            
            while (currentPosition <= length - ROOM_STARTING_POSITION) {
                // 20% chance to skip a room
                if (Math.random() > this.ROOM_PROBABILITY) {
                    currentPosition += Math.floor(Math.random() * 10) + 1;
                    continue;
                }
                
                // Calculate the room position along the tunnel
                const roomX = startX + direction.x * currentPosition;
                const roomZ = startZ + direction.z * currentPosition;
                
                // Get room dimensions
                const { width, height } = this.getRandomRoomSize();
                
                // Offset from tunnel
                let tunnelOffset = Math.floor(TUNNEL_WIDTH / 2) + 2;
                
                const roomStartX = roomX + perpDirection.x * tunnelOffset;
                const roomStartZ = roomZ + perpDirection.z * tunnelOffset;
                
                // Check if we can place a room here
                if (this.isAreaAvailable(roomStartX, roomStartZ, width, height, perpDirection)) {
                    // Create room
                    this.createRoom(roomStartX, roomStartZ, width, height, perpDirection);
                    
                    // Connect room to tunnel
                    this.connectRoomToTunnel(roomStartX, roomStartZ, width, height, roomX, roomZ, perpDirection);
                    
                    // Add room to list
                    this.rooms.push({
                        x: roomStartX,
                        z: roomStartZ,
                        width,
                        height
                    });
                    
                    // Move position forward by at least half the room width + 2 to ensure spacing
                    const minSpacing = Math.floor(width / 2) + 3;
                    currentPosition += minSpacing;
                } else {
                    // Try with a slightly smaller room
                    const scaleFactor = Math.random() * 0.2 + 0.5; // Random between 0.5 and 0.7
                    const smallerHeight = Math.max(this.MIN_ROOM_SIZE.height, Math.floor(height * scaleFactor));
                    const smallerWidth = Math.max(this.MIN_ROOM_SIZE.width, Math.floor(width * scaleFactor));
                    
                    if (this.isAreaAvailable(roomStartX, roomStartZ, smallerWidth, smallerHeight, perpDirection)) {
                        this.createRoom(roomStartX, roomStartZ, smallerWidth, smallerHeight, perpDirection);
                        this.connectRoomToTunnel(roomStartX, roomStartZ, smallerWidth, smallerHeight, roomX, roomZ, perpDirection);
                        
                        currentPosition += Math.floor(smallerWidth / 2) + 3;
                        continue;
                    }
                    
                    // Move forward a bit and try again
                    currentPosition += 3;
                }
            }
        }
    }

    /**
     * Helper to get the direction name from a Direction object
     * @param {Direction} dir - Direction object
     * @returns {string} - Direction name ('NORTH', 'EAST', 'SOUTH', 'WEST')
     */
    private getDirectionName(dir: Direction): string {
        if (dir.x === 0 && dir.z === -1) return 'NORTH';
        if (dir.x === 1 && dir.z === 0) return 'EAST';
        if (dir.x === 0 && dir.z === 1) return 'SOUTH';
        if (dir.x === -1 && dir.z === 0) return 'WEST';
        return 'NONE';
    }
}

// Create a global instance
const tunnelAlgorithm = new TunnelAlgorithm();
export default tunnelAlgorithm; 